import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SkeletonCard, SkeletonText, SkeletonMetric } from '../components/ui/Skeletons';
import { Loader2, TrendingUp, AlertCircle, CheckCircle, ArrowRight, Download, ChevronDown, ChevronUp, FileText, FileSpreadsheet, X } from 'lucide-react';
import axios from 'axios';
import PageSEO from '../components/PageSEO';
// ── Build full checklist with labels, status, category, and detail text ──
function buildChecklist(report) {
  if (!report) return [];
  const seo = report.seoAnalysis || {};
  const cnt = report.contentAnalysis || {};
  const perf = report.performanceMetrics || {};
  return [
    { label: `The SEO title is ${seo.title ? 'set' : 'missing'}.`, pass: !!seo.title, critical: true, category: 'seo', detail: seo.title ? `Title: "${seo.title}"` : 'Add a descriptive <title> tag to improve search result visibility and click-through rates.' },
    { label: `The meta description is ${seo.metaDescription ? 'set' : 'missing'}.`, pass: !!seo.metaDescription, critical: true, category: 'seo', detail: seo.metaDescription ? `Description: "${seo.metaDescription}"` : 'Add a compelling meta description (150–160 characters) to improve search snippet quality.' },
    { label: `${seo.h1Count || 0} H1 tag(s) found on the page.`, pass: (seo.h1Count || 0) > 0, critical: true, category: 'seo', detail: (seo.h1Count || 0) > 0 ? 'Good – your page has a proper H1 heading for structure and SEO.' : 'Every page should have exactly one H1 tag that describes its main topic.' },
    { label: `Canonical URL is ${seo.hasCanonical ? 'set' : 'not set'}.`, pass: !!seo.hasCanonical, critical: false, category: 'seo', detail: seo.hasCanonical ? 'Good – canonical tag prevents duplicate content issues.' : 'Add a <link rel="canonical"> tag to tell search engines which version of the page is authoritative.' },
    { label: `Mobile Viewport is ${seo.hasMobileViewport ? 'present' : 'missing'}.`, pass: !!seo.hasMobileViewport, critical: true, category: 'seo', detail: seo.hasMobileViewport ? 'Your site is configured for mobile devices.' : 'Add a <meta name="viewport"> tag to ensure proper rendering on mobile devices.' },
    { label: `${cnt.paragraphCount || 0} Paragraphs found.`, pass: (cnt.paragraphCount || 0) > 3, critical: false, category: 'content', detail: (cnt.paragraphCount || 0) > 3 ? 'Good content volume for search engines and visitors.' : 'Consider adding more content paragraphs to engage visitors and improve SEO.' },
    { label: `${cnt.imageCount || 0} Images found.`, pass: (cnt.imageCount || 0) > 0, critical: false, category: 'content', detail: (cnt.imageCount || 0) > 0 ? 'Visual content found – ensure images have alt text for accessibility.' : 'Add images to make your page more engaging and visually appealing.' },
    { label: `${cnt.linkCount || 0} Links found on page.`, pass: (cnt.linkCount || 0) > 0, critical: false, category: 'content', detail: (cnt.linkCount || 0) > 0 ? 'Internal and external links help with navigation and SEO.' : 'Add links to improve navigation and provide context for search engines.' },
    { label: `Clear Call-to-Action is ${cnt.hasCTA ? 'present' : 'missing'}.`, pass: !!cnt.hasCTA, critical: true, category: 'content', detail: cnt.hasCTA ? 'Good – a CTA guides visitors toward your goal (sign up, purchase, etc.).' : 'Add a clear call-to-action button like "Get Started" or "Contact Us" to guide visitors.' },
    { label: `${perf.asyncScripts || 0} Asynchronous Scripts.`, pass: (perf.asyncScripts || 0) > 0, critical: false, category: 'performance', detail: (perf.asyncScripts || 0) > 0 ? `${perf.asyncScripts} scripts use async/defer – this reduces render-blocking.` : 'Consider adding async or defer attributes to scripts to improve page load speed.' },
    { label: `${perf.scriptCount || 0} Scripts loaded.`, pass: (perf.scriptCount || 0) <= 15, critical: false, category: 'performance', detail: (perf.scriptCount || 0) <= 15 ? 'Script count is within acceptable range.' : 'Too many scripts can slow page load. Consider combining or removing unused scripts.' },
    { label: `${perf.stylesheetCount || 0} Stylesheets found.`, pass: (perf.stylesheetCount || 0) <= 5, critical: false, category: 'performance', detail: (perf.stylesheetCount || 0) <= 5 ? 'Good stylesheet organization.' : 'Multiple stylesheets increase render time. Consider consolidating CSS files.' },
  ];
}

// ── Metrics from checklist ──────────────────────────────────────────────
function computeMetrics(report) {
  const checks = buildChecklist(report);
  if (checks.length === 0) return { score: 0, label: 'N/A', total: 0, critical: 0, recommended: 0, good: 0 };
  const total = checks.length;
  const good = checks.filter(c => c.pass).length;
  const critical = checks.filter(c => !c.pass && c.critical).length;
  const recommended = total - good - critical;
  const score = Math.round((good / total) * 100);
  let label = 'Needs Work';
  if (score >= 80) label = 'Excellent';
  else if (score >= 60) label = 'Very Good';
  else if (score >= 40) label = 'Fair';
  return { score, label, total, critical, recommended, good };
}

// ── CSV builder ─────────────────────────────────────────────────────────
function buildReportCSV(report, metrics) {
  const rows = [];
  const add = (t = '') => rows.push(t);
  const sep = () => add('');
  add('AI Search Advisers Website Intelligence Report');
  add(`URL,${report.url}`);
  add(`Generated For,${report.clientName || 'N/A'}`);
  add(`Email,${report.clientEmail || 'N/A'}`);
  add(`Company,${report.clientCompany || 'N/A'}`);
  add(`Industry,${report.clientIndustry || 'N/A'}`);
  add(`Date,${report.createdAt ? new Date(report.createdAt).toLocaleString() : 'N/A'}`);
  sep();
  add('OVERALL SCORE');
  add(`Score,${metrics.score}/100`);
  add(`Rating,${metrics.label}`);
  add(`All Items,${metrics.total}`);
  add(`Critical Issues,${metrics.critical}`);
  add(`Recommended,${metrics.recommended}`);
  add(`Good Results,${metrics.good}`);
  sep();
  add('KEY FINDINGS');
  add(`Strength,"${report.summary?.strengths || 'N/A'}"`);
  add(`Opportunity,"${report.summary?.opportunities || 'N/A'}"`);
  add(`Quick Win,"${report.summary?.quickWins || 'N/A'}"`);
  sep();
  const seo = report.seoAnalysis || {};
  add('SEO ANALYSIS');
  add(`Title Tag,${seo.title ? 'Present' : 'Missing'}`);
  add(`Meta Description,${seo.metaDescription ? 'Present' : 'Missing'}`);
  add(`H1 Tags,${seo.h1Count || 0}`);
  add(`H2 Tags,${seo.h2Count || 0}`);
  add(`Canonical Tag,${seo.hasCanonical ? 'Present' : 'Missing'}`);
  add(`Mobile Viewport,${seo.hasMobileViewport ? 'Present' : 'Missing'}`);
  if (seo.strengths?.length) { add('SEO Strengths'); seo.strengths.forEach(s => add(`,"${s}"`)); }
  if (seo.weaknesses?.length) { add('SEO Weaknesses'); seo.weaknesses.forEach(w => add(`,"${w}"`)); }
  sep();
  const cnt = report.contentAnalysis || {};
  add('CONTENT ANALYSIS');
  add(`Paragraphs,${cnt.paragraphCount || 0}`);
  add(`Images,${cnt.imageCount || 0}`);
  add(`Links,${cnt.linkCount || 0}`);
  add(`Content Length,${cnt.contentLength || 0} chars`);
  add(`Call-to-Action,${cnt.hasCTA ? 'Present' : 'Missing'}`);
  if (cnt.strengths?.length) { add('Content Strengths'); cnt.strengths.forEach(s => add(`,"${s}"`)); }
  if (cnt.weaknesses?.length) { add('Content Weaknesses'); cnt.weaknesses.forEach(w => add(`,"${w}"`)); }
  sep();
  const perf = report.performanceMetrics || {};
  add('PERFORMANCE');
  add(`Scripts,${perf.scriptCount || 0}`);
  add(`Stylesheets,${perf.stylesheetCount || 0}`);
  add(`Images,${perf.imageCount || 0}`);
  add(`Async Scripts,${perf.asyncScripts || 0}`);
  add(`Render-Blocking Resources,${perf.renderBlockingCount || 0}`);
  if (perf.strengths?.length) { add('Performance Strengths'); perf.strengths.forEach(s => add(`,"${s}"`)); }
  if (perf.weaknesses?.length) { add('Performance Weaknesses'); perf.weaknesses.forEach(w => add(`,"${w}"`)); }
  sep();
  if (report.opportunities?.length) { add('OPPORTUNITIES'); report.opportunities.forEach(o => add(`,"${o}"`)); sep(); }
  if (report.quickWins?.length) { add('QUICK WINS'); report.quickWins.forEach(q => add(`,"${q}"`)); sep(); }
  add('© AI Search Advisers – Privacy-First Analytics Platform');
  return rows.join('\n');
}

// ── Single expandable row component ─────────────────────────────────────
function CheckRow({ item, isExpanded, onToggle }) {
  return (
    <div className="border-b border-slate-100 dark:border-slate-800">
      <button onClick={onToggle} className="w-full flex justify-between items-center py-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors px-2 rounded-none group">
        <div className="flex items-center gap-4">
          {item.pass ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" /> : item.critical ? <AlertCircle className="w-5 h-5 text-red-500 shrink-0" /> : <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />}
          <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base text-left">{item.label}</span>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 shrink-0 transition-colors" /> : <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 shrink-0 transition-colors" />}
      </button>
      {isExpanded && (
        <div className="pb-4 px-2 pl-14 animate-in slide-in-from-top-1 duration-200">
          <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{item.detail}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WebsiteIntelligenceReport() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [expandedRows, setExpandedRows] = useState({});
  const [cardFilter, setCardFilter] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const downloadRef = useRef(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(`/public/website-report/${reportId}`);
        setReport(response.data);
        setLoading(false);
      } catch (err) {
        setError('Could not load report. Please try again.');
        setLoading(false);
      }
    };
    if (reportId) fetchReport();
  }, [reportId]);

  // Close download menu on outside click
  useEffect(() => {
    const handler = (e) => { if (downloadRef.current && !downloadRef.current.contains(e.target)) setShowDownloadMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  // Download CSV
  const handleCSV = useCallback(() => {
    if (!report) return;
    const m = computeMetrics(report);
    const csv = buildReportCSV(report, m);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const domain = report.url ? new URL(report.url).hostname.replace('www.', '') : 'website';
    a.download = `aisearchadvisors_report_${domain}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  }, [report]);

  // Download PDF – expand everything first so all data is captured
  const handlePDF = useCallback(() => {
    setShowDownloadMenu(false);
    // Build keys for every expandable row and expand them all
    const allKeys = {};
    const cl = buildChecklist(report);
    cl.forEach((_, i) => {
      allKeys[`cf-${i}`] = true;
      allKeys[`seo-${i}`] = true;
      allKeys[`cnt-${i}`] = true;
      allKeys[`perf-${i}`] = true;
    });
    allKeys['seo-h2'] = true;
    allKeys['perf-rb'] = true;
    allKeys['perf-img'] = true;
    // Overview rows
    allKeys['ov-0'] = true;
    allKeys['ov-1'] = true;
    allKeys['ov-2'] = true;
    // Opportunities and quick wins
    (report.opportunities || []).forEach((_, i) => { allKeys[`opp-${i}`] = true; });
    (report.quickWins || []).forEach((_, i) => { allKeys[`win-${i}`] = true; });
    setExpandedRows(allKeys);
    setCardFilter(null);
    setPrintMode(true);
    // Wait for React to re-render with all sections visible, then print
    setTimeout(() => {
      window.print();
      // Restore state after print dialog closes
      setPrintMode(false);
      setExpandedRows({});
    }, 400);
  }, [report]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background transition-colors p-6 sm:p-8">
        <PageSEO title="Website Intelligence Report | AI Search Advisers" isPrivate={true} />
        <div className="max-w-7xl mx-auto flex flex-col items-center mb-12">
          <SkeletonText width="w-96" height="h-10" className="mb-8" />
          <div className="flex w-full justify-between items-center">
             <SkeletonText width="w-32" height="h-10" />
             <SkeletonText width="w-32" height="h-10" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto mb-8">
          <SkeletonCard className="flex flex-col md:flex-row gap-8 p-8">
             <div className="flex-1">
               <SkeletonText width="w-64" height="h-8" className="mb-4" />
               <SkeletonText width="w-full" height="h-4" className="mb-2" />
               <SkeletonText width="w-3/4" height="h-4" />
             </div>
             <div className="w-48 h-48 rounded-full border-8 border-gray-200 dark:border-[#202020] skeleton-neutralizer shrink-0" style={{ boxShadow: 'none', filter: 'none', outline: 'none' }} />
          </SkeletonCard>
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
             <SkeletonMetric key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-background dark:to-slate-900 flex items-center justify-center transition-colors">
        <PageSEO title="Website Intelligence Report | AI Search Advisers" isPrivate={true} />
        <div className="max-w-md text-center bg-white dark:bg-card rounded-2xl shadow-lg border border-transparent dark:border-border p-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-700 dark:text-slate-300 font-semibold mb-4">{error || 'Report not found'}</p>
          <Button onClick={() => navigate('/')} className="w-full bg-[#FEAB3D] text-[#021426] font-semibold rounded-lg hover:bg-[#e8960a]">Back to Home</Button>
        </div>
      </div>
    );
  }

  const metrics = computeMetrics(report);
  const checklist = buildChecklist(report);
  const dashOffset = 251.2 - (251.2 * metrics.score / 100);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'seo', label: 'SEO Analysis' },
    { id: 'content', label: 'Content' },
    { id: 'performance', label: 'Performance' },
    { id: 'opportunities', label: 'Opportunities' }
  ];

  // Filter checklist items based on card filter
  const getFilteredItems = () => {
    if (!cardFilter || cardFilter === 'all') return checklist;
    if (cardFilter === 'critical') return checklist.filter(c => !c.pass && c.critical);
    if (cardFilter === 'recommended') return checklist.filter(c => !c.pass && !c.critical);
    if (cardFilter === 'good') return checklist.filter(c => c.pass);
    return checklist;
  };

  const cardFilterLabels = { all: 'All Items', critical: 'Critical Issues', recommended: 'Recommended Improvements', good: 'Good Results' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-background dark:to-slate-900 transition-colors">
      <PageSEO title="Website Intelligence Report | AI Search Advisers" isPrivate={true} />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white text-center mb-8">Your Website Intelligence Report is here!</h1>

        {/* Top Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center bg-white dark:bg-card shadow-sm border border-slate-200 dark:border-slate-800 rounded-lg p-4 mb-8 gap-4">
          <div className="flex items-center gap-3 w-full sm:w-auto overflow-hidden">
            <div className="w-8 h-8 flex items-center justify-center bg-slate-50 dark:bg-slate-700/50 rounded shadow-sm border border-slate-200 dark:border-slate-600 shrink-0">
               <TrendingUp className="w-5 h-5 text-[#021426] dark:text-[#FEAB3D]" />
            </div>
            <span className="font-medium text-slate-800 dark:text-slate-200 truncate">{report.url}</span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
             <span className="text-sm font-medium text-slate-500 dark:text-slate-400 hidden sm:block">Generated for {report.clientName}</span>
             {/* Download Dropdown */}
             <div className="relative" ref={downloadRef}>
               <Button onClick={() => setShowDownloadMenu(prev => !prev)} className="bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-bold h-10 px-6 transition-transform hover:scale-105 shadow-md">
                 <Download className="w-4 h-4 mr-2" />
                 Download Report
                 <ChevronDown className="w-4 h-4 ml-2" />
               </Button>
               {showDownloadMenu && (
                 <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-card border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                   <button onClick={handlePDF} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                     <FileText className="w-4 h-4 text-red-500" />
                     Download as PDF
                   </button>
                   <div className="border-t border-slate-100 dark:border-slate-700" />
                   <button onClick={handleCSV} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                     <FileSpreadsheet className="w-4 h-4 text-green-500" />
                     Download as CSV
                   </button>
                 </div>
               )}
             </div>
          </div>
        </div>

        {/* Main Score Area */}
        <div className="bg-slate-50 dark:bg-card border border-slate-200 dark:border-border rounded-2xl p-8 mb-8 flex flex-col md:flex-row justify-between items-center gap-8 shadow-sm">
          <div className="max-w-md text-center md:text-left">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Overall Site Score</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-lg">
              A very good score is between 60 and 80. For best results, you should strive for 70 and above.
            </p>
          </div>
          <div className="relative w-48 h-48 flex items-center justify-center flex-shrink-0">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-200 dark:text-slate-800" />
              <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="251.2" strokeDashoffset={dashOffset} strokeLinecap="round" className="text-[#FEAB3D]" />
            </svg>
            <div className="absolute flex flex-col items-center justify-center text-center">
              <div className="text-5xl font-bold text-slate-900 dark:text-white">{metrics.score}<span className="text-xl text-slate-500 dark:text-slate-400 font-medium">/100</span></div>
              <div className="text-sm font-bold text-[#021426] dark:text-[#FEAB3D] mt-1">{metrics.label}</div>
            </div>
          </div>
        </div>

        {/* Clickable Summary Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {[
            { key: 'all', count: metrics.total, total: metrics.total, label: 'All Items', color: 'slate', borderClass: 'border-l-slate-700 dark:border-l-slate-500', textClass: 'text-slate-900 dark:text-white' },
            { key: 'critical', count: metrics.critical, total: metrics.total, label: 'Critical Issues', color: 'red', borderClass: 'border-l-red-500 dark:border-l-red-500', textClass: 'text-red-600 dark:text-red-400' },
            { key: 'recommended', count: metrics.recommended, total: metrics.total, label: 'Recommended', color: 'blue', borderClass: 'border-l-[#FEAB3D] dark:border-l-[#FEAB3D]', textClass: 'text-[#FEAB3D] dark:text-blue-400' },
            { key: 'good', count: metrics.good, total: metrics.total, label: 'Good Results', color: 'green', borderClass: 'border-l-green-500 dark:border-l-green-500', textClass: 'text-green-600 dark:text-green-400' },
          ].map(card => (
            <button
              key={card.key}
              onClick={() => { setCardFilter(prev => prev === card.key ? null : card.key); setSelectedTab('overview'); }}
              className={`bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-5 border-l-4 ${card.borderClass} shadow-sm flex flex-col justify-center text-left transition-all hover:-translate-y-1 hover:shadow-md cursor-pointer ${cardFilter === card.key ? 'ring-2 ring-[#FEAB3D] ring-offset-2 dark:ring-offset-slate-900' : ''}`}
            >
              <div className={`text-3xl font-bold ${card.textClass} mb-1`}>{card.count} <span className="text-sm text-slate-500 dark:text-slate-400 font-normal">of {card.total}</span></div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">{card.label}</div>
            </button>
          ))}
        </div>

        {/* Card-filter panel: show filtered items when a card is clicked */}
        {cardFilter && (
          <div className="mb-12 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-2xl p-6 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">{cardFilterLabels[cardFilter]}</h3>
              <button onClick={() => setCardFilter(null)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            <div className="space-y-1">
              {getFilteredItems().length === 0 ? (
                <div className="flex items-center gap-4 py-4">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-slate-800 dark:text-slate-200 font-medium">No items in this category.</span>
                </div>
              ) : (
                getFilteredItems().map((item, idx) => (
                  <CheckRow key={`cf-${idx}`} item={item} isExpanded={expandedRows[`cf-${idx}`]} onToggle={() => toggleRow(`cf-${idx}`)} />
                ))
              )}
            </div>
          </div>
        )}

        {/* Layout: Sidebar + Main Content */}
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Sidebar – hidden during print */}
          <div className={`lg:col-span-3 space-y-12 ${printMode ? 'hidden' : ''}`}>
            <div className="flex flex-col space-y-2">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => { setSelectedTab(tab.id); setCardFilter(null); }}
                  className={`text-left px-5 py-3 rounded-lg font-semibold transition-colors ${selectedTab === tab.id ? 'bg-slate-100 dark:bg-slate-800/80 text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                >{tab.label}</button>
              ))}
            </div>
          </div>

          {/* Right Content Panel */}
          <div className={`${printMode ? 'lg:col-span-12' : 'lg:col-span-9'} space-y-12`}>
            {/* OVERVIEW TAB */}
            {(selectedTab === 'overview' || printMode) && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-4">Key Findings</h2>
                <div className="space-y-1">
                  {[
                    { icon: <CheckCircle className="w-5 h-5 text-green-500" />, text: `Strength: ${report.summary?.strengths || 'Website has a good foundation'}`, detail: 'This is an area where your website already performs well. Maintain these best practices as you continue to grow.' },
                    { icon: <AlertCircle className="w-5 h-5 text-amber-500" />, text: `Opportunity: ${report.summary?.opportunities || 'Room for optimization'}`, detail: 'Addressing this opportunity can significantly improve your website\'s visibility and user experience.' },
                    { icon: <TrendingUp className="w-5 h-5 text-[#FEAB3D]" />, text: `Quick Win: ${report.summary?.quickWins || 'Priority improvements'}`, detail: 'This is a high-impact, low-effort improvement you can implement right away for immediate results.' },
                  ].map((item, idx) => (
                    <div key={`ov-${idx}`} className="border-b border-slate-100 dark:border-slate-800">
                      <button onClick={() => toggleRow(`ov-${idx}`)} className="w-full flex justify-between items-center py-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors px-2 rounded-lg group">
                        <div className="flex items-center gap-4">
                          {item.icon}
                          <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base text-left">{item.text}</span>
                        </div>
                        {expandedRows[`ov-${idx}`] ? <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 shrink-0" />}
                      </button>
                      {expandedRows[`ov-${idx}`] && (
                        <div className="pb-4 px-2 pl-14 animate-in slide-in-from-top-1 duration-200">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700">
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{item.detail}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-6">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Executive Summary</h3>
                  <div className="p-6 bg-slate-50 dark:bg-card border border-slate-200 dark:border-border rounded-xl">
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                      This report provides an AI-powered analysis of your website using publicly available data. Our system examined your site's structure, content, SEO signals, and technical indicators to identify strengths and opportunities for improvement.
                    </p>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                      The recommendations in this report are based on industry best practices and web performance standards. Implementation of these suggestions can help improve search visibility, user experience, and conversion potential.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SEO TAB */}
            {(selectedTab === 'seo' || printMode) && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-4">Basic SEO</h2>
                <div className="space-y-1">
                  {checklist.filter(c => c.category === 'seo').map((item, idx) => (
                    <CheckRow key={`seo-${idx}`} item={item} isExpanded={expandedRows[`seo-${idx}`]} onToggle={() => toggleRow(`seo-${idx}`)} />
                  ))}
                  {/* H2 count */}
                  <CheckRow
                    item={{ label: `${report.seoAnalysis?.h2Count || 0} H2 tag(s) found on the page.`, pass: (report.seoAnalysis?.h2Count || 0) > 0, critical: false, detail: (report.seoAnalysis?.h2Count || 0) > 0 ? 'H2 tags help structure your content and improve readability for both users and search engines.' : 'Add H2 headings to break up your content into scannable sections.' }}
                    isExpanded={expandedRows['seo-h2']}
                    onToggle={() => toggleRow('seo-h2')}
                  />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-4 mt-12">Search Preview</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Here is how the site may appear in search results:</p>
                <div className="p-6 border border-slate-200 dark:border-slate-700 shadow-sm rounded-lg bg-white dark:bg-card">
                   <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{report.url}</div>
                   <div className="text-xl text-[#FEAB3D] dark:text-blue-400 font-medium mb-2 hover:underline cursor-pointer">{report.seoAnalysis?.title || report.clientName || 'Page Title'}</div>
                   <div className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                     {report.seoAnalysis?.metaDescription || 'No meta description set. Add a descriptive meta tag to improve how your site appears in search results.'}
                   </div>
                </div>
              </div>
            )}

            {/* CONTENT TAB */}
            {(selectedTab === 'content' || printMode) && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-4">Content & Messaging</h2>
                <div className="space-y-1">
                  {checklist.filter(c => c.category === 'content').map((item, idx) => (
                    <CheckRow key={`cnt-${idx}`} item={item} isExpanded={expandedRows[`cnt-${idx}`]} onToggle={() => toggleRow(`cnt-${idx}`)} />
                  ))}
                </div>
                {(report.contentAnalysis?.strengths?.length > 0 || report.contentAnalysis?.weaknesses?.length > 0) && (
                  <div className="mt-8 space-y-4">
                    {report.contentAnalysis.strengths?.map((s, i) => (
                      <div key={`cs-${i}`} className="flex items-center gap-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                        <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base">{s}</span>
                      </div>
                    ))}
                    {report.contentAnalysis.weaknesses?.map((w, i) => (
                      <div key={`cw-${i}`} className="flex items-center gap-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                        <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base">{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PERFORMANCE TAB */}
            {(selectedTab === 'performance' || printMode) && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-4">Performance</h2>
                <div className="space-y-1">
                  {checklist.filter(c => c.category === 'performance').map((item, idx) => (
                    <CheckRow key={`perf-${idx}`} item={item} isExpanded={expandedRows[`perf-${idx}`]} onToggle={() => toggleRow(`perf-${idx}`)} />
                  ))}
                  {/* Render-blocking */}
                  <CheckRow
                    item={{ label: `${report.performanceMetrics?.renderBlockingCount || 0} Render-blocking resources.`, pass: (report.performanceMetrics?.renderBlockingCount || 0) <= 5, critical: false, detail: (report.performanceMetrics?.renderBlockingCount || 0) <= 5 ? 'Your render-blocking resource count is within acceptable limits.' : 'Too many render-blocking resources delay page display. Consider inlining critical CSS and deferring non-essential resources.' }}
                    isExpanded={expandedRows['perf-rb']}
                    onToggle={() => toggleRow('perf-rb')}
                  />
                  {/* Images */}
                  <CheckRow
                    item={{ label: `${report.performanceMetrics?.imageCount || 0} Images on page.`, pass: (report.performanceMetrics?.imageCount || 0) <= 10, critical: false, detail: (report.performanceMetrics?.imageCount || 0) <= 10 ? 'Image count is manageable. Ensure images are compressed and use modern formats (WebP).' : 'High image count may impact loading speed. Lazy-load below-the-fold images and compress file sizes.' }}
                    isExpanded={expandedRows['perf-img']}
                    onToggle={() => toggleRow('perf-img')}
                  />
                </div>
                {(report.performanceMetrics?.strengths?.length > 0 || report.performanceMetrics?.weaknesses?.length > 0) && (
                  <div className="mt-8 space-y-4">
                    {report.performanceMetrics.strengths?.map((s, i) => (
                      <div key={`ps-${i}`} className="flex items-center gap-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                        <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base">{s}</span>
                      </div>
                    ))}
                    {report.performanceMetrics.weaknesses?.map((w, i) => (
                      <div key={`pw-${i}`} className="flex items-center gap-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                        <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base">{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* OPPORTUNITIES TAB */}
            {(selectedTab === 'opportunities' || printMode) && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-4">Opportunities</h2>
                <div className="space-y-1">
                  {(report.opportunities || []).length === 0 && (report.quickWins || []).length === 0 && (
                    <div className="flex items-center gap-4 py-4">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base">Great job! No major opportunities identified.</span>
                    </div>
                  )}
                  {(report.opportunities || []).map((opp, idx) => (
                    <div key={`opp-${idx}`} className="border-b border-slate-100 dark:border-slate-800">
                      <button onClick={() => toggleRow(`opp-${idx}`)} className="w-full flex justify-between items-center py-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors px-2 rounded-lg group">
                        <div className="flex items-center gap-4">
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                          <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base text-left">{opp}</span>
                        </div>
                        {expandedRows[`opp-${idx}`] ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                      </button>
                      {expandedRows[`opp-${idx}`] && (
                        <div className="pb-4 px-2 pl-14 animate-in slide-in-from-top-1 duration-200">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700">
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">Addressing this issue will improve your website's overall score and user experience. Consider implementing a fix as part of your next development sprint.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {(report.quickWins || []).map((win, idx) => (
                    <div key={`win-${idx}`} className="border-b border-slate-100 dark:border-slate-800">
                      <button onClick={() => toggleRow(`win-${idx}`)} className="w-full flex justify-between items-center py-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors px-2 rounded-lg group">
                        <div className="flex items-center gap-4">
                          <CheckCircle className="w-5 h-5 text-[#FEAB3D]" />
                          <span className="text-slate-800 dark:text-slate-200 font-medium text-sm sm:text-base text-left">{win}</span>
                        </div>
                        {expandedRows[`win-${idx}`] ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                      </button>
                      {expandedRows[`win-${idx}`] && (
                        <div className="pb-4 px-2 pl-14 animate-in slide-in-from-top-1 duration-200">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700">
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">This is a quick win that can be implemented with minimal effort. Prioritize these changes for immediate impact on your website's quality.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action CTA Section */}
        <div className="mt-20 border-t border-slate-200 dark:border-border pt-16 text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">Ready to Dive Deeper?</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto text-lg">
            This report analyzed your website using public data only. Activate our full analytics platform to track user behavior, see heatmaps, and identify conversion bottlenecks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => { window.scrollTo(0, 0); navigate('/pricing'); }}
              className="bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-bold px-10 py-6 text-lg rounded-xl shadow-lg transition-transform hover:-translate-y-1"
            >
              Start Free Trial
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 px-10 py-6 text-lg rounded-xl transition-all"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
